import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useTranslation } from 'react-i18next';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = '/img/favicon.png',
  ogType = 'website',
}) => {
  const { t } = useTranslation();

  const defaultTitle = t('app_name');
  const defaultDescription = t('app_description');
  const defaultKeywords = t('app_keywords');

  const fullTitle = title ? `${title} | ${defaultTitle}` : defaultTitle;
  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  const canonicalUrl = canonical
    ? `${siteUrl}${canonical}`
    : `${siteUrl}${window.location.pathname}`;
  const imageUrl = ogImage.startsWith('http')
    ? ogImage
    : `${siteUrl}${ogImage}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={fullTitle} />
      <meta
        property="og:description"
        content={description || defaultDescription}
      />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={defaultTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta
        name="twitter:description"
        content={description || defaultDescription}
      />
      <meta name="twitter:image" content={imageUrl} />

      <meta name="robots" content="index, follow" />
      <meta name="author" content={defaultTitle} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      <link rel="apple-touch-icon" href="/img/favicon.png" />
      <link rel="icon" type="image/png" href="/img/favicon.png" />
    </Helmet>
  );
};
